/**
 * SearchForm Component
 * Consolidated search form for power inquiry - optimized for bail agent workflow
 */

import { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingOfficeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { OathButton, OathInput, OathSelect } from '../../../shared/OathUI';
import { OathCheckbox } from '../../../shared/OathTableComponents';
import { DATE_TYPES, VIEW_MODES } from '../../../../services/oathPowerInquiryService';

/**
 * Consolidated search form for power inquiry
 */
const SearchForm = ({
  // Agency selection
  agencies = [],
  selectedAgencyIds = [],
  onToggleAgency,
  onSelectAllAgencies,
  onClearAgencySelection,

  // View mode
  viewMode = VIEW_MODES.INDIVIDUAL,
  onViewModeChange,

  // Search fields
  defendantLastName,
  onDefendantLastNameChange,
  defendantFirstName,
  onDefendantFirstNameChange,
  powerNumber,
  onPowerNumberChange,
  batchId,
  onBatchIdChange,
  reportNo,
  onReportNoChange,

  // Date filtering
  dateType,
  onDateTypeChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,

  // Group-scoped filters (e.g., AOL bucket radio buttons)
  selectedGroupFilter,
  groupScopedFilters = [],
  selectedGroupScopedFilter,
  onGroupScopedFilterChange,

  // Actions
  onSearch,
  onClear,
  onExport,

  // States
  isSearching = false,
  isExporting = false,
  hasSearchCriteria = false,
  hasResults = false,
  onCollapseChange,
  collapseToken, // External trigger - when this changes (and is truthy), collapse the form

  // Filter badges for collapsed view
  filterBadges = [], // Array of { label, type: 'group' | 'specific' }
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Auto-collapse when results are populated
  useEffect(() => {
    if (hasResults) {
      setIsCollapsed(true);
    }
  }, [hasResults]);

  // External collapse trigger (e.g., from pagination interaction)
  useEffect(() => {
    if (collapseToken && hasResults) {
      setIsCollapsed(true);
    }
  }, [collapseToken, hasResults]);

  // Notify parent when collapse state changes (with delay to let animation complete)
  useEffect(() => {
    // Delay notification to prevent fixed header from blinking during animation
    const timer = setTimeout(() => {
      onCollapseChange?.(isCollapsed);
    }, isCollapsed ? 350 : 0); // Delay when collapsing, immediate when expanding

    return () => clearTimeout(timer);
  }, [isCollapsed, onCollapseChange]);

  const dateTypeOptions = [
    { value: DATE_TYPES.NONE, label: 'No Date Filter' },
    { value: DATE_TYPES.EXECUTION, label: 'Execution Date' },
    { value: DATE_TYPES.DUE_DATES, label: 'Due Date' },
    { value: DATE_TYPES.REPORT, label: 'Report Date' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch();
    setIsCollapsed(true);
  };

  // Check if any search fields have values
  const hasSearchValues = defendantLastName || defendantFirstName || powerNumber || reportNo || batchId || dateType !== DATE_TYPES.NONE || selectedGroupScopedFilter;
  const hasDateFilter = dateType !== DATE_TYPES.NONE;

  // Build criteria badges for collapsed view
  const criteriaBadges = [];
  if (defendantLastName) criteriaBadges.push({ label: `Last: ${defendantLastName}`, type: 'criteria' });
  if (defendantFirstName) criteriaBadges.push({ label: `First: ${defendantFirstName}`, type: 'criteria' });
  if (powerNumber) criteriaBadges.push({ label: `Power: ${powerNumber}`, type: 'criteria' });
  if (reportNo) criteriaBadges.push({ label: `Report: ${reportNo}`, type: 'criteria' });
  if (batchId) criteriaBadges.push({ label: `Batch: ${batchId}`, type: 'criteria' });
  if (hasDateFilter) {
    const dateLabel = dateTypeOptions.find(opt => opt.value === dateType)?.label || 'Date Filter';
    // Format date to short US locale (M/D/YY)
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-');
      return `${parseInt(month)}/${parseInt(day)}/${year.slice(-2)}`;
    };
    // Format date range string
    let dateRange = '';
    if (startDate && endDate) {
      dateRange = `: ${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      dateRange = `: from ${formatDate(startDate)}`;
    } else if (endDate) {
      dateRange = `: to ${formatDate(endDate)}`;
    }
    criteriaBadges.push({ label: `${dateLabel}${dateRange}`, type: 'criteria' });
  }

  // Combine all badges: filters first, then criteria
  const allBadges = [...filterBadges, ...criteriaBadges];

  return (
    <form onSubmit={handleSubmit}>
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-300 dark:border-gray-700 overflow-hidden">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        {/* Accordion Header - Show when results exist OR when already collapsed */}
        {(hasResults || isCollapsed) && (
          <div className="border-b border-gray-300 dark:border-gray-700">
            {/* Clickable Accordion Toggle Bar */}
            <button
              type="button"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="w-full px-4 sm:px-5 py-2.5 sm:py-3 flex items-center justify-between bg-gray-100 dark:bg-gray-900/50 hover:bg-gray-200 dark:hover:bg-gray-800/70 transition-colors cursor-pointer group"
            >
              {/* Left side - Title and badges */}
              <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                <MagnifyingGlassIcon className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
                <span className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">Search</span>
                {/* Mobile: Show compact filter count */}
                {isCollapsed && (selectedAgencyIds.length > 0 || allBadges.length > 0) && (
                  <span className="sm:hidden px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light text-xs font-medium rounded-full">
                    {selectedAgencyIds.length + allBadges.length} filter{(selectedAgencyIds.length + allBadges.length) !== 1 ? 's' : ''}
                  </span>
                )}
                {/* Desktop: Show agency count badge */}
                {selectedAgencyIds.length > 0 && (
                  <span className="hidden sm:inline-flex px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light text-xs font-medium rounded-full">
                    {selectedAgencyIds.length} {selectedAgencyIds.length === 1 ? 'agency' : 'agencies'}
                  </span>
                )}
                {/* Desktop: Filter and criteria badges - show first 4 when collapsed */}
                {isCollapsed && allBadges.slice(0, 4).map((badge, index) => (
                  <span
                    key={index}
                    className={`hidden sm:inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      badge.type === 'group'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                        : badge.type === 'specific'
                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    }`}
                  >
                    {badge.label}
                  </span>
                ))}
                {isCollapsed && allBadges.length > 4 && (
                  <span
                    className="hidden sm:inline-flex px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-full"
                    title={allBadges.slice(4).map(b => b.label).join('\n')}
                  >
                    +{allBadges.length - 4} more
                  </span>
                )}
              </div>

              {/* Right side - Toggle indicator */}
              <div className="flex items-center gap-1.5 sm:gap-2 ml-2 sm:ml-4 flex-shrink-0">
                <span className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
                  {isCollapsed ? 'Show' : 'Hide'}
                </span>
                <div className={`p-1 rounded-full bg-gray-200 dark:bg-gray-700 group-hover:bg-gray-300 dark:group-hover:bg-gray-600 transition-all ${isCollapsed ? '' : 'rotate-180'}`}>
                  <ChevronDownIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                </div>
              </div>
            </button>

            {/* Action Bar - Only visible in accordion header when collapsed (moves to bottom when expanded) */}
            <div
              className={`px-4 sm:px-5 py-2 sm:py-2.5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:justify-between bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 ${isCollapsed ? '' : 'hidden'}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Mobile: Search button full width at top */}
              <div className="sm:hidden flex gap-2">
                <OathButton
                  type="submit"
                  size="sm"
                  icon={MagnifyingGlassIcon}
                  loading={isSearching}
                  disabled={selectedAgencyIds.length === 0}
                  className="flex-1"
                >
                  Search
                </OathButton>
                <OathButton
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={ArrowDownTrayIcon}
                  onClick={onExport}
                  loading={isExporting}
                  disabled={!hasSearchCriteria || isSearching}
                  title={!hasSearchCriteria ? 'Enter search criteria to enable export' : undefined}
                  className="flex-1"
                >
                  Export
                </OathButton>
              </div>

              {/* Mobile: Quick inputs row */}
              <div className="sm:hidden flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Power #</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={powerNumber}
                      onChange={(e) => onPowerNumberChange(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Report #</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={reportNo}
                      onChange={(e) => onReportNoChange(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* View Mode Toggle + Clear (mobile: bottom row) */}
              <div className="flex items-center justify-between sm:justify-start gap-3">
                <div className="flex gap-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewModeChange?.(VIEW_MODES.INDIVIDUAL);
                    }}
                    className={`px-2 sm:px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                      viewMode === VIEW_MODES.INDIVIDUAL
                        ? 'bg-white dark:bg-gray-600 text-primary dark:text-primary-light shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    By Power
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewModeChange?.(VIEW_MODES.BY_REPORT);
                    }}
                    className={`px-2 sm:px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                      viewMode === VIEW_MODES.BY_REPORT
                        ? 'bg-white dark:bg-gray-600 text-primary dark:text-primary-light shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    By Report
                  </button>
                </div>
                {/* Mobile: Clear button inline */}
                <OathButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={XMarkIcon}
                  onClick={onClear}
                  disabled={isSearching}
                  className="sm:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Clear
                </OathButton>
              </div>

              {/* Desktop: Quick inputs centered between toggle and buttons */}
              <div className="hidden sm:flex items-center justify-center gap-3 flex-1">
                <div className="flex-1 max-w-[200px]">
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Power #</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={powerNumber}
                      onChange={(e) => onPowerNumberChange(e.target.value)}
                      placeholder="Search power..."
                      className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
                <div className="flex-1 max-w-[200px]">
                  <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-0.5">Report #</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={reportNo}
                      onChange={(e) => onReportNoChange(e.target.value)}
                      placeholder="Search report..."
                      className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Desktop: Action buttons */}
              <div className="hidden sm:flex items-center gap-2">
                <OathButton
                  type="submit"
                  size="sm"
                  icon={MagnifyingGlassIcon}
                  loading={isSearching}
                  disabled={selectedAgencyIds.length === 0}
                >
                  Search
                </OathButton>
                <OathButton
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={ArrowDownTrayIcon}
                  onClick={onExport}
                  loading={isExporting}
                  disabled={!hasSearchCriteria || isSearching}
                  title={!hasSearchCriteria ? 'Enter search criteria to enable export' : undefined}
                >
                  Export
                </OathButton>
                <OathButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={XMarkIcon}
                  onClick={onClear}
                  disabled={isSearching}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Clear
                </OathButton>
              </div>
            </div>
          </div>
        )}

        {/* Main Search Section - Collapsible with animation */}
        <div
          className={`grid transition-all duration-300 ease-in-out ${
            isCollapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
          }`}
        >
          <div className="overflow-hidden">
          <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
            {/* Agency Selection Card — only show when multiple agencies */}
            {agencies.length > 1 && <div className="bg-gray-100 dark:bg-gray-900/50 rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden">
              <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BuildingOfficeIcon className="h-4 w-4 text-primary dark:text-primary-light" />
                    <span className="font-medium text-sm text-gray-900 dark:text-white">Agencies</span>
                    {selectedAgencyIds.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light text-xs font-medium rounded-full">
                        {selectedAgencyIds.length}/{agencies.length}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={onSelectAllAgencies}
                      className="text-xs font-medium text-primary dark:text-primary-light hover:underline"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={onClearAgencySelection}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      None
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1 max-h-28 sm:max-h-24 overflow-y-auto">
                  {agencies.map((agency) => {
                    const isSelected = selectedAgencyIds.includes(agency.agencyId);
                    return (
                      <label
                        key={agency.agencyId}
                        className={`flex items-center gap-2 cursor-pointer p-2 rounded-md transition-colors group ${
                          isSelected
                            ? 'bg-primary-50 dark:bg-primary-900/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <OathCheckbox
                          checked={isSelected}
                          onChange={() => onToggleAgency(agency.agencyId)}
                          size="sm"
                        />
                        <span className={`text-sm truncate ${
                          isSelected
                            ? 'text-gray-900 dark:text-white font-medium'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {agency.agencyId} - {agency.dbaAgencyName}
                        </span>
                        <span className={`text-xs flex-shrink-0 ${
                          isSelected
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          ({agency.agencyId})
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>}

            {/* Search Criteria Card - Consolidated */}
            <div className="bg-gray-100 dark:bg-gray-900/50 rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden">
              <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-primary dark:text-primary-light" />
                  <span className="font-medium text-sm text-gray-900 dark:text-white">Search Criteria</span>
                  {hasSearchValues && (
                    <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-full">
                      Active
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                {/* Row 1: Identifiers - 2 cols on mobile for Power/Report, full row for Batch */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <OathInput
                    label="Power #"
                    value={powerNumber}
                    onChange={onPowerNumberChange}
                    placeholder="Exact"
                  />
                  <OathInput
                    label="Report #"
                    value={reportNo}
                    onChange={onReportNoChange}
                    placeholder="Report"
                  />
                  <OathInput
                    label="Batch ID"
                    type="text"
                    inputMode="numeric"
                    value={batchId}
                    onChange={(value) => {
                      // Only allow digits, no other characters
                      const digitsOnly = value.replace(/\D/g, '');
                      // Remove leading zeros (except when field is empty)
                      const normalized = digitsOnly.replace(/^0+/, '') || '';
                      onBatchIdChange(normalized);
                    }}
                    placeholder="Batch"
                    className="col-span-2 sm:col-span-1"
                  />
                </div>

                {/* Row 2: Defendant */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <OathInput
                    label="Last Name"
                    value={defendantLastName}
                    onChange={onDefendantLastNameChange}
                    placeholder="e.g., Smith"
                  />
                  <OathInput
                    label="First Name"
                    value={defendantFirstName}
                    onChange={onDefendantFirstNameChange}
                    placeholder="e.g., John"
                  />
                </div>

                {/* Row 3: Date Filtering — disabled when an AOL bucket is selected (backend handles dates) */}
                <div
                  className={`space-y-3 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4${selectedGroupScopedFilter ? ' opacity-50' : ''}`}
                  title={selectedGroupScopedFilter ? 'Date filter is unavailable while a liability age is selected' : undefined}
                >
                  <OathSelect
                    label="Date Filter"
                    value={dateType}
                    onChange={onDateTypeChange}
                    options={dateTypeOptions}
                    disabled={!!selectedGroupScopedFilter}
                  />
                  <div className="grid grid-cols-2 gap-3 sm:contents">
                    <OathInput
                      label="Start"
                      type="date"
                      value={startDate}
                      onChange={onStartDateChange}
                      disabled={!hasDateFilter || !!selectedGroupScopedFilter}
                      className={!hasDateFilter || selectedGroupScopedFilter ? 'opacity-50' : ''}
                    />
                    <OathInput
                      label="End"
                      type="date"
                      value={endDate}
                      onChange={onEndDateChange}
                      disabled={!hasDateFilter || !!selectedGroupScopedFilter}
                      className={!hasDateFilter || selectedGroupScopedFilter ? 'opacity-50' : ''}
                    />
                  </div>
                </div>

                {/* Row 4: Liability Age (AOL bucket radio buttons) — only when Open Liability group filter is active, disabled when date filter is applied */}
                {groupScopedFilters.length > 0 && groupScopedFilters.some(f => f.requiresGroupFilter === selectedGroupFilter) && (
                  <div
                    className={hasDateFilter ? 'opacity-50' : ''}
                    title={hasDateFilter ? 'Liability age is unavailable while a date filter is applied' : undefined}
                  >
                    <label className="block text-[10px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                      Liability Age
                    </label>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      <button
                        type="button"
                        disabled={hasDateFilter}
                        onClick={() => onGroupScopedFilterChange(null)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                          !selectedGroupScopedFilter
                            ? 'bg-primary/10 dark:bg-primary/20 border-primary/30 dark:border-primary/40 text-primary dark:text-primary-light'
                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary/30 hover:text-primary dark:hover:text-primary-light'
                        } ${hasDateFilter ? 'cursor-not-allowed' : ''}`}
                      >
                        All
                      </button>
                      {groupScopedFilters
                        .filter(f => f.requiresGroupFilter === selectedGroupFilter)
                        .map(filter => (
                          <button
                            key={filter.filterName}
                            type="button"
                            disabled={hasDateFilter}
                            onClick={() => onGroupScopedFilterChange(filter.filterName)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                              selectedGroupScopedFilter === filter.filterName
                                ? 'bg-primary/10 dark:bg-primary/20 border-primary/30 dark:border-primary/40 text-primary dark:text-primary-light'
                                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary/30 hover:text-primary dark:hover:text-primary-light'
                            } ${hasDateFilter ? 'cursor-not-allowed' : ''}`}
                          >
                            {filter.label || filter.filterDescription}
                          </button>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Bottom Action Bar - shows when expanded (moves from accordion header to bottom) or before first search */}
        {!isCollapsed && (
          <div className="px-4 sm:px-5 py-3 sm:py-4 bg-gray-100 dark:bg-gray-900/50 border-t border-gray-300 dark:border-gray-700">
            {/* Mobile Layout */}
            <div className="sm:hidden space-y-3">
              {/* Search button - prominent */}
              <OathButton
                type="submit"
                icon={MagnifyingGlassIcon}
                loading={isSearching}
                disabled={selectedAgencyIds.length === 0}
                className="w-full"
              >
                Search
              </OathButton>

              {/* Secondary row: View mode + Export + Clear */}
              <div className="flex items-center gap-2">
                <div className="flex gap-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => onViewModeChange?.(VIEW_MODES.INDIVIDUAL)}
                    className={`px-3 py-1.5 sm:px-2 sm:py-1 text-xs font-medium rounded-md transition-all ${
                      viewMode === VIEW_MODES.INDIVIDUAL
                        ? 'bg-white dark:bg-gray-600 text-primary dark:text-primary-light shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Power
                  </button>
                  <button
                    type="button"
                    onClick={() => onViewModeChange?.(VIEW_MODES.BY_REPORT)}
                    className={`px-3 py-1.5 sm:px-2 sm:py-1 text-xs font-medium rounded-md transition-all ${
                      viewMode === VIEW_MODES.BY_REPORT
                        ? 'bg-white dark:bg-gray-600 text-primary dark:text-primary-light shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Report
                  </button>
                </div>
                <OathButton
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={ArrowDownTrayIcon}
                  onClick={onExport}
                  loading={isExporting}
                  disabled={!hasSearchCriteria || isSearching}
                  className="flex-1"
                >
                  Export
                </OathButton>
                <OathButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={XMarkIcon}
                  onClick={onClear}
                  disabled={isSearching}
                  className="text-gray-500"
                >
                  Clear
                </OathButton>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex gap-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => onViewModeChange?.(VIEW_MODES.INDIVIDUAL)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                    viewMode === VIEW_MODES.INDIVIDUAL
                      ? 'bg-white dark:bg-gray-600 text-primary dark:text-primary-light shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  By Power
                </button>
                <button
                  type="button"
                  onClick={() => onViewModeChange?.(VIEW_MODES.BY_REPORT)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                    viewMode === VIEW_MODES.BY_REPORT
                      ? 'bg-white dark:bg-gray-600 text-primary dark:text-primary-light shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  By Report
                </button>
              </div>

              {/* Primary Actions */}
              <div className="flex gap-3 flex-1">
                <OathButton
                  type="submit"
                  icon={MagnifyingGlassIcon}
                  loading={isSearching}
                  disabled={selectedAgencyIds.length === 0}
                  className="min-w-[100px] sm:min-w-[140px]"
                >
                  Search
                </OathButton>
                <OathButton
                  type="button"
                  variant="outline"
                  icon={ArrowDownTrayIcon}
                  onClick={onExport}
                  loading={isExporting}
                  disabled={!hasSearchCriteria || isSearching}
                  title={!hasSearchCriteria ? 'Enter search criteria to enable export' : undefined}
                >
                  Export CSV
                </OathButton>
              </div>

              {/* Secondary Actions */}
              <OathButton
                type="button"
                variant="ghost"
                icon={XMarkIcon}
                onClick={onClear}
                disabled={isSearching}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear All
              </OathButton>
            </div>

            {/* Helper text */}
            {selectedAgencyIds.length === 0 && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Please select at least one agency to search
              </p>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

export default SearchForm;
